import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Product } from '../data/products';

export interface CartItem {
  product: Product;
  quantity: number;
}

interface StoreState {
  cart: CartItem[];
  favorites: number[];
  language: string;
  searchOpen: boolean;
  cartOpen: boolean;

  addToCart: (product: Product, quantity?: number) => void;
  removeFromCart: (productId: number) => void;
  updateQuantity: (productId: number, quantity: number) => void;
  clearCart: () => void;
  getCartTotal: () => number;
  getCartCount: () => number;

  toggleFavorite: (productId: number) => void;
  isFavorite: (productId: number) => boolean;

  setLanguage: (lang: string) => void;
  setSearchOpen: (open: boolean) => void;
  setCartOpen: (open: boolean) => void;

  generateOrderNumber: () => string;
}

export const useStore = create<StoreState>()(
  persist(
    (set, get) => ({
      cart: [],
      favorites: [],
      language: 'tr',
      searchOpen: false,
      cartOpen: false,

      addToCart: (product, quantity = 1) => {
        set((state) => {
          const existing = state.cart.find((item) => item.product.id === product.id);
          if (existing) {
            return {
              cart: state.cart.map((item) =>
                item.product.id === product.id
                  ? { ...item, quantity: item.quantity + quantity }
                  : item
              ),
            };
          }
          return { cart: [...state.cart, { product, quantity }] };
        });
      },

      removeFromCart: (productId) => {
        set((state) => ({
          cart: state.cart.filter((item) => item.product.id !== productId),
        }));
      },

      updateQuantity: (productId, quantity) => {
        set((state) => ({
          cart:
            quantity <= 0
              ? state.cart.filter((item) => item.product.id !== productId)
              : state.cart.map((item) =>
                  item.product.id === productId ? { ...item, quantity } : item
                ),
        }));
      },

      clearCart: () => set({ cart: [] }),

      getCartTotal: () => {
        return get().cart.reduce((total, item) => {
          const price = item.product.salePrice > 0 ? item.product.salePrice : item.product.regularPrice;
          return total + price * item.quantity;
        }, 0);
      },

      getCartCount: () => {
        return get().cart.reduce((total, item) => total + item.quantity, 0);
      },

      toggleFavorite: (productId) => {
        set((state) => ({
          favorites: state.favorites.includes(productId)
            ? state.favorites.filter((id) => id !== productId)
            : [...state.favorites, productId],
        }));
      },

      isFavorite: (productId) => {
        return get().favorites.includes(productId);
      },

      setLanguage: (lang) => set({ language: lang }),
      setSearchOpen: (open) => set({ searchOpen: open }),
      setCartOpen: (open) => set({ cartOpen: open }),

      generateOrderNumber: () => {
        const now = new Date();
        const prefix = 'TRX';
        const date = now.getFullYear().toString().slice(2) +
          String(now.getMonth() + 1).padStart(2, '0') +
          String(now.getDate()).padStart(2, '0');
        const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
        // Paytr merchant_oid alanı yalnızca alfanumerik karakter kabul eder;
        // tire (-) veya başka özel karakter içeremez.
        return `${prefix}${date}${random}`;
      },
    }),
    {
      name: 'trax-store',
      partialize: (state) => ({
        cart: state.cart,
        favorites: state.favorites,
        language: state.language,
      }),
    }
  )
);
